import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth-store'
import { triggerRevalidation } from '@/lib/utils'
import type { Tables, TablesInsert, TablesUpdate } from '@/types/supabase'

type Event = Tables<'events'>
type IdolMember = Tables<'idol_members'>

export function useEvents() {
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useQuery({
    queryKey: ['events', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('tenant_id', tenantId!)
        .order('starts_at', { ascending: true })

      if (error) throw error
      return data as Event[]
    },
  })
}

export function useEvent(id: string | undefined) {
  return useQuery({
    queryKey: ['event', id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('id', id!)
        .single()
      if (error) throw error
      return data as Event
    },
  })
}

export function useCreateEvent() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async (input: Omit<TablesInsert<'events'>, 'tenant_id'>) => {
      const { data, error } = await supabase
        .from('events')
        .insert({ ...input, tenant_id: tenantId! })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['events', tenantId] })
      triggerRevalidation('tenant-events')
    },
  })
}

export function useUpdateEvent() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async ({ id, ...update }: TablesUpdate<'events'> & { id: string }) => {
      const { data, error } = await supabase
        .from('events')
        .update(update)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['events', tenantId] })
      triggerRevalidation('tenant-events')
    },
  })
}

export function useDeleteEvent() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('events').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['events', tenantId] })
      triggerRevalidation('tenant-events')
    },
  })
}

export function useEventLineup(eventId: string | undefined) {
  return useQuery({
    queryKey: ['event-lineup', eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_members')
        .select('idol_members(*)')
        .eq('event_id', eventId!)
      if (error) throw error
      return (data ?? [])
        .map((r: { idol_members: IdolMember | null }) => r.idol_members)
        .filter(Boolean) as IdolMember[]
    },
  })
}

export function useUpsertEventLineup() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({ eventId, memberIds }: { eventId: string; memberIds: string[] }) => {
      const { error: delErr } = await supabase
        .from('event_members')
        .delete()
        .eq('event_id', eventId)
      if (delErr) throw delErr

      if (memberIds.length) {
        const { error: insErr } = await supabase
          .from('event_members')
          .insert(memberIds.map((mid) => ({ event_id: eventId, member_id: mid })))
        if (insErr) throw insErr
      }
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['event-lineup', vars.eventId] })
    },
  })
}

// ── Setlist ──────────────────────────────────────────────────────────────────

type SetlistItem = Tables<'event_setlist'>

export function useEventSetlist(eventId: string | undefined) {
  return useQuery({
    queryKey: ['event-setlist', eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_setlist')
        .select('*')
        .eq('event_id', eventId!)
        .order('order_num', { ascending: true })
      if (error) throw error
      return (data ?? []) as SetlistItem[]
    },
  })
}

export function useUpsertEventSetlist() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async ({
      eventId,
      items,
    }: {
      eventId: string
      items: { song_type: string; track_id: string | null; cover_id: string | null; order_num: number }[]
    }) => {
      const { error: delErr } = await supabase
        .from('event_setlist')
        .delete()
        .eq('event_id', eventId)
      if (delErr) throw delErr

      if (items.length) {
        const { error: insErr } = await supabase
          .from('event_setlist')
          .insert(items.map((item) => ({ ...item, event_id: eventId, tenant_id: tenantId! })))
        if (insErr) throw insErr
      }
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['event-setlist', vars.eventId] })
    },
  })
}
